import * as yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { Flex, Space, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { PasswordInput } from '@common/components';
import { validationPattern } from '@common/helpers';
import { useFormFocus } from '@common/hooks';
import { AuthFormHoc } from 'src/hoc';
import { IPasswordValues } from '@common/types/types';
import { PATHS } from 'src/constants';
import { setIsAuthorizeAction, useAppDispatch } from 'src/store';

const initialValues: IPasswordValues = {
  password: '',
  confirmPassword: '',
};

const validationFormSchema = yup.object().shape({
  password: validationPattern.password,
  confirmPassword: validationPattern.confirmPassWord,
});

const ResetPasswordForm = (): JSX.Element => {
  const navigate = useNavigate();
  const [t] = useTranslation('auth');
  const dispatch = useAppDispatch();
  const urlParams = new URLSearchParams(location.search);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const email: string = urlParams.get('email') as string;
  const code: string = urlParams.get('code') as string;

  const form = useForm({
    initialValues: initialValues,
    validate: yupResolver(validationFormSchema),
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });

  const [systemError, setSystemError] = useState<null | string>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (systemError) {
      errorRef.current?.setAttribute('tabindex', '0');
      errorRef.current?.focus();
    }
  }, [systemError]);

  const onSubmitHandler = async (values: IPasswordValues): Promise<void> => {
    setIsLoading(true);
    setNeedFocus(true);
    setSystemError(null);
    try {
      await Auth.forgotPasswordSubmit(email, code, values.password);
      await Auth.signIn(email, values.password);
      dispatch(setIsAuthorizeAction(true));
      navigate(PATHS.MAIN);
    } catch (error) {
      if (error instanceof Error) {
        setSystemError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onErrorHandler = (): void => {
    setNeedFocus(true);
    setSystemError(null);
  };

  return (
    <AuthFormHoc
      title={`${t('createNewPassword')}`}
      description={<p>{t('createNewPasswordDescription')}</p>}
      form={form}
      submitText={`${t('savePassword')}`}
      onSubmitHandler={onSubmitHandler}
      onErrorHandler={onErrorHandler}
      isLoading={isLoading}>
      <>
        {systemError && (
          <Flex ref={errorRef}>
            <Text color={'#a80000'}>{systemError}</Text>
          </Flex>
        )}
        <PasswordInput
          label={t('password')}
          placeholder={t('createPassword')}
          ariaLabel={`${t('passwordDescribe')}`}
          {...form.getInputProps('password')}
        />
        <Space h={'md'} />
        <PasswordInput
          label={`${t('confirm')} ${t('password')}`}
          placeholder={`${t('confirm')} ${t('password')}`}
          ariaLabel={`${t('confirmPasswordDescribe')}`}
          {...form.getInputProps('confirmPassword')}
        />
      </>
    </AuthFormHoc>
  );
};

export default ResetPasswordForm;
